// Migrated to ES6 class + ol@10 compat:
// - removed inherits() from ol/index.js → extends
// - removed SourceState (string literals)
// - removed expandUrl from ol/tileurlfunction.js (reimplementado inline)
import { hash } from 'ol/tilecoord.js';
import { modulo } from 'ol/math.js';

import XYZSource from 'ol/source/XYZ.js';

import * as extent from 'ol/extent';
import * as proj from 'ol/proj.js';

import HereImagerySet from '../lov/HereImagerySet';

/*eslint no-self-assign: "off"*/
/*eslint default-case: "off"*/

/**
 * Reimplementação local de expandUrl (removido de ol/tileurlfunction.js em ol@7+).
 * Expande templates com `{a-z}` em múltiplas URLs.
 *
 * @example
 *   expandUrl('https://{1-4}.tile.com/') → ['https://1.tile.com/', '...2...', '...3...', '...4...']
 *
 * @param {string} url
 * @return {string[]}
 */
function expandUrl(url) {
  const urls = [];
  // Suporta ranges alfanuméricos: {a-z}, {A-Z}, {0-9}, {1-4}, etc.
  const match = url.match(/\{([a-zA-Z0-9])-([a-zA-Z0-9])\}/);
  if (!match) {
    return [url];
  }
  const start = match[1].charCodeAt(0);
  const end = match[2].charCodeAt(0);
  for (let c = start; c <= end; c++) {
    urls.push(url.replace(match[0], String.fromCharCode(c)));
  }
  return urls;
}

function doGet(url, callback) {
  var xhr = new XMLHttpRequest();

  xhr.open('GET', url, true);

  xhr.onload = () => {
    if (xhr.status === 200) {
      var response = JSON.parse(xhr.response);
      callback(response, xhr.status);
    }
    else {
      callback(null, xhr.status);
    }
  };

  xhr.send();
}

/**
 * Source HERE Maps. Usa endpoint Here Hereapi v2.1.
 *
 * @extends {XYZSource}
 */
class HereSource extends XYZSource {
  constructor(options) {
    super({
      attributions: options.attributions,
      cacheSize: options.cacheSize,
      extent: options.extent,
      opaque: options.opaque,
      projection: 'EPSG:3857',
      state: options.state,
      tileGrid: options.tileGrid,
      tilePixelRatio: options.tilePixelRatio,
      wrapX: options.wrapX === undefined ? true : options.wrapX,
      transition: options.transition,
    });

    this._imagerySet = options.imagerySet;
    this._key = options.key;

    // Set tileUrlFunction depois do super() pra ter acesso a this
    this.setTileUrlFunction(this.defaultCreateTileFunction(options.imagerySet, options.key));

    var settings = HereSource.getImagerySettings(options.imagerySet);

    if (!settings) return;

    const copyrightTemplateUrl =
      'https://{1-4}.{base}.maps.ls.hereapi.com/maptile/2.1/copyright/newest?apiKey={api_key}&output=json';

    var index = 2;

    var url = copyrightTemplateUrl
      .replace('{1-4}', index)
      .replace('{base}', settings.base)
      .replace('{api_key}', options.key);

    doGet(url, this.handleCopyrightMetadataResponse.bind(this));
  }

  _generateUrlTileCoord(tileCoord) {
    var wrappedCoord = this.getTileCoordForTileUrlFunction(tileCoord);
    return [wrappedCoord[0], wrappedCoord[1], -wrappedCoord[2] - 1];
  }

  defaultCreateTileFunction(imagerySet, key) {
    var hereUrlTemplate =
      'https://{1-4}.{base}.maps.ls.hereapi.com' +
      '/{type}/2.1/maptile/newest/{scheme}/{z}/{x}/{y}/256/{format}' +
      '?apiKey={api_key}';

    var settings = HereSource.getImagerySettings(imagerySet);

    if (settings === null) return null;

    var templateUrl = hereUrlTemplate
      .replace('{base}', settings.base)
      .replace('{type}', settings.type)
      .replace('{scheme}', settings.imagerySet)
      .replace('{format}', settings.format)
      .replace('{api_key}', key);

    var urls = expandUrl(templateUrl);

    var tileUrlFunction = (tileCoord) => {
      if (!tileCoord) return '';

      var url = urls[0];

      if (urls.length > 1) {
        var h = hash(tileCoord);
        var index = modulo(h, urls.length);
        url = urls[index];
      }

      var wrappedCoord = this._generateUrlTileCoord(tileCoord);

      return url
        .replace('{z}', wrappedCoord[0])
        .replace('{x}', wrappedCoord[1])
        .replace('{y}', wrappedCoord[2]);
    };

    return tileUrlFunction;
  }

  static getImagerySettings(imagerySet) {
    var settings = null;

    switch (imagerySet) {
      case HereImagerySet.ROADMAP:
        settings = { base: 'base', type: 'maptile', imagerySet: 'normal.day', format: 'jpg' };
        break;
      case HereImagerySet.SATELLITE:
        settings = { base: 'aerial', type: 'maptile', imagerySet: 'satellite.day', format: 'jpg' };
        break;
      case HereImagerySet.HYBRID:
        settings = { base: 'aerial', type: 'maptile', imagerySet: 'hybrid.day', format: 'jpg' };
        break;
      case HereImagerySet.TERRAIN:
        settings = { base: 'aerial', type: 'maptile', imagerySet: 'terrain.day', format: 'jpg' };
        break;
      default:
        settings = null;
        break;
    }

    return settings;
  }

  handleCopyrightMetadataResponse(response, statusCode) {
    if (statusCode !== 200 || !response) {
      this.setState('error');
      return;
    }

    var imageryType = this._imagerySet.split('.')[0];
    var copyrightData = (this._copyrightData = response[imageryType]);

    var wrapX = this.getWrapX();
    var maxExtent = this.getProjection().getExtent();
    var maxExtentWidth = extent.getWidth(maxExtent);

    this.setAttributions((frameState) => {
      var copyright = '&copy; ' + new Date().getFullYear() + ' HERE';

      var attributions = [copyright];
      var zoom = frameState.viewState.zoom;

      var viewExtent = frameState.extent.slice();

      // wraps extents
      if (wrapX) {
        if (viewExtent[0] < maxExtent[0]) {
          while (viewExtent[0] < maxExtent[0]) {
            viewExtent[0] += maxExtentWidth;
            viewExtent[2] += maxExtentWidth;
          }
        }
        else if (viewExtent[0] > maxExtent[2]) {
          while (viewExtent[0] > maxExtent[2]) {
            viewExtent[0] -= maxExtentWidth;
            viewExtent[2] -= maxExtentWidth;
          }
        }
      }

      copyrightData.forEach(function (imageryProvider) {
        if (zoom < imageryProvider.minLevel || zoom > imageryProvider.maxLevel) return;

        var intersects = !imageryProvider.boxes;

        if (!intersects) {
          var idx = imageryProvider.boxes.findIndex((box) => {
            var geographicCoords = [
              [box[1], box[0]],
              [box[3], box[2]],
            ];
            var projectedCoords = geographicCoords.map((x) => proj.fromLonLat(x));
            var boxExtent = extent.boundingExtent(projectedCoords);
            return extent.intersects(boxExtent, viewExtent);
          });

          intersects = idx > -1;
        }

        if (intersects) attributions.push(imageryProvider.label);
      });

      return [
        attributions.join(', '),
        ' | <a href="https://legal.here.com/br-pt/terms/serviceterms">Terms of Use</a>',
      ];
    });

    this.setState('ready');
  }
}

export default HereSource;
